import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Wrench, Plus, Pencil, Trash2, X, Car, LayoutList, Columns3, Download, CarFront, ShoppingCart, Archive, ArchiveRestore, Package, Search, FileText } from "lucide-react";
import { toast } from "sonner";

const STATUTS = {
  diagnostic: { label: "En diagnostic", color: "bg-cyan-500/15 text-cyan-400 border-cyan-500/30" },
  commande_piece: { label: "En commande pièces", color: "bg-amber-500/15 text-amber-400 border-amber-500/30" },
  piece_recue: { label: "Pièce reçue", color: "bg-violet-500/15 text-violet-400 border-violet-500/30" },
  reparation: { label: "En réparation", color: "bg-blue-500/15 text-blue-400 border-blue-500/30" },
  termine: { label: "Terminé / Prêt", color: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" },
  annule: { label: "Annulé", color: "bg-red-500/15 text-red-400 border-red-500/30" },
};

const EMPTY_VEH = { marque: "", modele: "", immatriculation: "", annee: "", kilometrage: 0 };

const EMPTY = {
  vehicle_id: "", client_id: "", statut: "diagnostic", kilometrage: 0, diagnostic: "",
  main_oeuvre: [], pieces_utilisees: [], commentaires: "",
};

export default function Repairs() {
  const { isFull } = useAuth();
  const navigate = useNavigate();
  const [repairs, setRepairs] = useState([]);
  const [preview, setPreview] = useState(null);
  const [archived, setArchived] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [clients, setClients] = useState([]);
  const [stock, setStock] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [selStock, setSelStock] = useState("");
  const [view, setView] = useState("liste");
  const [dragId, setDragId] = useState(null);
  const [order, setOrder] = useState({ designation: "", quantite: 1 });
  const [vehOpen, setVehOpen] = useState(false);
  const [vehForm, setVehForm] = useState(EMPTY_VEH);
  const [quickId, setQuickId] = useState(null);
  const [quickPiece, setQuickPiece] = useState({ designation: "", quantite: 1 });
  const [partQuery, setPartQuery] = useState("");
  const [partResults, setPartResults] = useState([]);
  const [partSearching, setPartSearching] = useState(false);

  const load = async () => {
    const [r, ar, v, c, s, cat] = await Promise.all([
      api.get("/repairs"), api.get("/repairs", { params: { archived: true } }),
      api.get("/vehicles"), api.get("/clients"), api.get("/stock"), api.get("/catalog"),
    ]);
    setRepairs(r.data); setArchived(ar.data); setVehicles(v.data); setClients(c.data); setStock(s.data); setCatalog(cat.data);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const ql = partQuery.trim();
    if (ql.length < 2) { setPartResults([]); setPartSearching(false); return; }
    setPartSearching(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/parts/search", { params: { q: ql } });
        setPartResults(data);
      } catch (e) { setPartResults([]); }
      finally { setPartSearching(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [partQuery]);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (r) => {
    setForm({
      vehicle_id: r.vehicle_id, client_id: r.client_id || "", statut: r.statut,
      kilometrage: r.kilometrage, diagnostic: r.diagnostic, main_oeuvre: r.main_oeuvre || [],
      pieces_utilisees: r.pieces_utilisees || [], commentaires: r.commentaires,
    });
    setEditId(r.id); setOpen(true);
  };

  const onVehicleChange = (vid) => {
    const veh = vehicles.find((v) => v.id === vid);
    setForm((f) => ({ ...f, vehicle_id: vid, client_id: veh?.client_id || "", kilometrage: veh?.kilometrage || f.kilometrage }));
  };

  const addMO = () => setForm((f) => ({ ...f, main_oeuvre: [...f.main_oeuvre, { description: "", heures: 1, taux: 60 }] }));
  const setMO = (i, k, v) => setForm((f) => { const m = [...f.main_oeuvre]; m[i] = { ...m[i], [k]: v }; return { ...f, main_oeuvre: m }; });
  const rmMO = (i) => setForm((f) => ({ ...f, main_oeuvre: f.main_oeuvre.filter((_, x) => x !== i) }));

  const addPiece = () => {
    const item = stock.find((s) => s.id === selStock);
    if (!item) return;
    setForm((f) => ({
      ...f,
      pieces_utilisees: [...f.pieces_utilisees, { stock_id: item.id, reference: item.reference, designation: item.designation, quantite: 1, prix: item.prix_vente, mo_incluse: true, source: "stock" }],
    }));
    setSelStock("");
  };
  const addPartFromSearch = (r) => {
    setForm((f) => ({
      ...f,
      pieces_utilisees: [...f.pieces_utilisees, { stock_id: r.stock_id || null, reference: r.reference || "", designation: r.designation, quantite: 1, prix: r.prix || 0, mo_incluse: true, source: r.source || "", vhu_part_id: r.source === "vhu" ? (r.part_id || null) : null }],
    }));
    setPartQuery(""); setPartResults([]);
  };
  const setPieceQty = (i, v) => setForm((f) => { const p = [...f.pieces_utilisees]; p[i] = { ...p[i], quantite: Number(v) }; return { ...f, pieces_utilisees: p }; });
  const setPiecePrix = (i, v) => setForm((f) => { const p = [...f.pieces_utilisees]; p[i] = { ...p[i], prix: Number(v) }; return { ...f, pieces_utilisees: p }; });
  const setPieceMO = (i, v) => setForm((f) => { const p = [...f.pieces_utilisees]; p[i] = { ...p[i], mo_incluse: v }; return { ...f, pieces_utilisees: p }; });
  const rmPiece = (i) => setForm((f) => ({ ...f, pieces_utilisees: f.pieces_utilisees.filter((_, x) => x !== i) }));

  const addOrderPiece = () => {
    if (!order.designation.trim()) return;
    const hit = catalog.find((c) => c.designation.toLowerCase() === order.designation.trim().toLowerCase());
    setForm((f) => ({
      ...f,
      pieces_utilisees: [...f.pieces_utilisees, { stock_id: null, reference: hit?.reference || "", designation: order.designation.trim(), quantite: Number(order.quantite) || 1, prix: hit?.prix || 0, commande: false, mo_incluse: true }],
    }));
    setOrder({ designation: "", quantite: 1 });
  };

  const saveVehicle = async () => {
    try {
      const { data } = await api.post("/vehicles", { ...vehForm, kilometrage: Number(vehForm.kilometrage) });
      toast.success("Véhicule créé (à rattacher à un client par le secrétariat)");
      const v = await api.get("/vehicles"); setVehicles(v.data);
      setForm((f) => ({ ...f, vehicle_id: data.id, client_id: data.client_id || "", kilometrage: data.kilometrage || f.kilometrage }));
      setVehOpen(false); setVehForm(EMPTY_VEH);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const downloadPdf = async (r) => {
    try {
      const res = await api.get(`/repairs/${r.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `ordre-reparation-${r.vehicle?.immatriculation || r.id}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Ordre de réparation téléchargé");
    } catch (e) { toast.error("Erreur lors de la génération du PDF"); }
  };

  const save = async () => {
    const payload = {
      ...form, kilometrage: Number(form.kilometrage),
      main_oeuvre: form.main_oeuvre.map((m) => ({ description: m.description, heures: Number(m.heures), taux: Number(m.taux) })),
      pieces_utilisees: form.pieces_utilisees.map((p) => ({ ...p, quantite: Number(p.quantite), prix: Number(p.prix) })),
    };
    try {
      if (editId) await api.put(`/repairs/${editId}`, payload);
      else await api.post("/repairs", payload);
      toast.success(editId ? "Fiche mise à jour (stock ajusté)" : "Fiche créée");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer cette fiche ?")) return;
    await api.delete(`/repairs/${id}`); toast.success("Fiche supprimée"); load();
  };

  const changeStatus = async (id, statut) => {
    const r = repairs.find((x) => x.id === id);
    if (!r || r.statut === statut) return;
    setRepairs((rs) => rs.map((x) => (x.id === id ? { ...x, statut } : x)));
    try {
      await api.patch(`/repairs/${id}/status`, { statut });
      if (statut === "termine") toast.success("Fiche terminée et archivée");
      else toast.success(`Statut : ${STATUTS[statut].label}`);
      if (statut === "annule" || statut === "termine") load();
    } catch (e) { load(); toast.error("Erreur de mise à jour"); }
  };

  const setArchivedState = async (id, value) => {
    try {
      await api.patch(`/repairs/${id}/archive`, { archived: value });
      toast.success(value ? "Fiche archivée" : "Fiche désarchivée");
      load();
    } catch (e) { toast.error("Erreur lors de l'archivage"); }
  };

  const markOrdered = async (id) => {
    try {
      await api.post(`/repairs/${id}/mark-ordered`);
      toast.success("Pièces marquées commandées");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const quickAddOrder = async () => {
    const r = repairs.find((x) => x.id === quickId);
    if (!r || !quickPiece.designation.trim()) return;
    const hit = catalog.find((c) => c.designation.toLowerCase() === quickPiece.designation.trim().toLowerCase());
    const pu = [...(r.pieces_utilisees || []), { stock_id: null, reference: hit?.reference || "", designation: quickPiece.designation.trim(), quantite: Number(quickPiece.quantite) || 1, prix: hit?.prix || 0, commande: false, mo_incluse: true }];
    try {
      await api.put(`/repairs/${r.id}`, {
        vehicle_id: r.vehicle_id, client_id: r.client_id || "", statut: r.statut, kilometrage: r.kilometrage,
        diagnostic: r.diagnostic, main_oeuvre: r.main_oeuvre || [], pieces_utilisees: pu, commentaires: r.commentaires || "",
      });
      toast.success("Pièce à commander ajoutée à la fiche");
      setQuickId(null); setQuickPiece({ designation: "", quantite: 1 });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const vLabel = (v) => v ? `${v.marque} ${v.modele} — ${v.immatriculation}` : "—";

  const PiecesApercu = ({ r, compact }) => {
    const ps = r.pieces_utilisees || [];
    if (ps.length === 0) return <div className="mt-2 text-[11px] text-[#64748B] italic">Aucune pièce</div>;
    const max = compact ? 2 : 4;
    const shown = ps.slice(0, max);
    const extra = ps.length - max;
    const nbExcept = ps.filter((p) => p.mo_incluse === false).length;
    return (
      <div data-testid={`repair-pieces-preview-${r.id}`} className="mt-2 flex flex-wrap items-center gap-1.5">
        <Package className="w-3.5 h-3.5 text-cyan-400 shrink-0" />
        {shown.map((p, i) => (
          <span key={i} className="text-[11px] px-1.5 py-0.5 rounded bg-[#0A0D14] border border-[#2A364F] text-[#CBD5E1]">
            {p.designation} <span className="text-[#64748B]">×{p.quantite}</span>
            {p.mo_incluse === false && <span className="ml-1 text-amber-400" title="Main d'œuvre en sus">MO+</span>}
          </span>
        ))}
        {extra > 0 && <span className="text-[11px] text-[#64748B]">+{extra} autre{extra > 1 ? "s" : ""}</span>}
        {!compact && nbExcept > 0 && <span className="text-[10px] text-amber-400">· MO en sus : {nbExcept}</span>}
      </div>
    );
  };

  return (
    <div className="space-y-5 animate-fade-up" data-testid="repairs-page">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-[#F8FAFC]">Réparations</h1>
          <p className="text-[#94A3B8] text-sm">Fiches de réparation par véhicule.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-lg border border-[#2A364F] overflow-hidden">
            <button data-testid="view-liste" onClick={() => setView("liste")} className={`px-3 py-2 text-sm flex items-center gap-1.5 ${view === "liste" ? "bg-[#3B82F6] text-white" : "bg-transparent text-[#94A3B8] hover:text-white"}`}><LayoutList className="w-4 h-4" />Liste</button>
            <button data-testid="view-atelier" onClick={() => setView("atelier")} className={`px-3 py-2 text-sm flex items-center gap-1.5 ${view === "atelier" ? "bg-[#3B82F6] text-white" : "bg-transparent text-[#94A3B8] hover:text-white"}`}><Columns3 className="w-4 h-4" />Atelier</button>
            <button data-testid="view-archive" onClick={() => setView("archive")} className={`px-3 py-2 text-sm flex items-center gap-1.5 ${view === "archive" ? "bg-[#3B82F6] text-white" : "bg-transparent text-[#94A3B8] hover:text-white"}`}><Archive className="w-4 h-4" />Archivées{archived.length > 0 && <span className="ml-1 text-[10px] font-mono bg-[#0A0D14] px-1.5 py-0.5 rounded">{archived.length}</span>}</button>
          </div>
          <Button data-testid="create-repair-button" onClick={openNew} className="bg-[#F59E0B] hover:bg-[#D97706] text-[#0A0D14] font-semibold">
            <Plus className="w-4 h-4 mr-2" /> Nouvelle fiche
          </Button>
        </div>
      </div>

      <div className={`grid gap-3 ${view === "liste" || view === "archive" ? "" : "hidden"}`}>
        {(view === "archive" ? archived : repairs).map((r) => {
          const st = STATUTS[r.statut] || STATUTS.diagnostic;
          const totMO = (r.main_oeuvre || []).reduce((a, m) => a + m.heures * m.taux, 0);
          const totP = (r.pieces_utilisees || []).reduce((a, p) => a + p.quantite * p.prix, 0);
          return (
            <div key={r.id} data-testid={`repair-card-${r.id}`} className="bg-[#182232] border border-[#2A364F] rounded-xl p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className={`text-[11px] px-2.5 py-1 rounded border ${st.color}`}>{st.label}</span>
                    <div className="flex items-center gap-2 text-[#F8FAFC] font-heading font-semibold"><Car className="w-4 h-4 text-cyan-400" />{vLabel(r.vehicle)}</div>
                    {r.vehicle?.immatriculation && <span className="font-mono font-bold tracking-widest text-yellow-400 bg-slate-900 px-2 py-0.5 rounded border border-yellow-500/30 uppercase text-xs">{r.vehicle.immatriculation}</span>}
                    {r.devis_numero && <button data-testid={`repair-from-devis-${r.id}`} onClick={() => navigate("/devis")} className="text-[10px] px-2 py-0.5 rounded border bg-blue-500/10 text-blue-400 border-blue-500/30 hover:bg-blue-500/20 flex items-center gap-1" title="Voir le devis d'origine"><FileText className="w-3 h-3" />Issu du devis {r.devis_numero}</button>}
                  </div>
                  <div className="text-sm text-[#94A3B8] mt-2">
                    {r.client?.nom && <span>Client : {r.client.nom} · </span>}
                    {r.kilometrage?.toLocaleString("fr-FR")} km · Mécano : {r.mecanicien || "—"}
                  </div>
                  {r.diagnostic && <p className="text-sm text-[#94A3B8] mt-2 line-clamp-2">🔧 {r.diagnostic}</p>}
                  <PiecesApercu r={r} />
                  <div className="flex gap-4 mt-3 text-xs">
                    <span className="text-[#64748B]">Pièces : <span className="font-mono text-cyan-400">{totP.toFixed(2)} €</span></span>
                    <span className="text-[#64748B]">Main d'œuvre : <span className="font-mono text-cyan-400">{totMO.toFixed(2)} €</span></span>
                    <span className="text-[#64748B]">Total : <span className="font-mono font-bold text-[#F59E0B]">{(totP + totMO).toFixed(2)} €</span></span>
                  </div>
                  <div className={`flex items-center gap-2 mt-3 flex-wrap ${view === "archive" ? "hidden" : ""}`}>
                    <span className="text-[10px] uppercase tracking-wider text-[#64748B]">Actions rapides :</span>
                    <Select value={r.statut} onValueChange={(v) => changeStatus(r.id, v)}>
                      <SelectTrigger data-testid={`quick-status-${r.id}`} className="h-8 w-44 bg-[#0A0D14] border-[#2A364F] text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>{Object.entries(STATUTS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                    </Select>
                    <Button data-testid={`quick-add-order-${r.id}`} size="sm" variant="outline" onClick={() => { setQuickId(r.id); setQuickPiece({ designation: "", quantite: 1 }); }} className="h-8 border-amber-500/40 bg-transparent text-amber-400 hover:bg-amber-500/10 hover:text-amber-300">
                      <Plus className="w-3.5 h-3.5 mr-1" /> Pièce à commander
                    </Button>
                    {isFull && (r.pieces_utilisees || []).some((p) => !p.stock_id && !p.commande) && (
                      <Button data-testid={`quick-order-${r.id}`} size="sm" onClick={() => markOrdered(r.id)} className="h-8 bg-amber-500 hover:bg-amber-600 text-[#0A0D14] font-semibold">
                        <ShoppingCart className="w-3.5 h-3.5 mr-1" /> Marquer commandé
                      </Button>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {view === "archive" ? (
                    <button data-testid={`unarchive-repair-${r.id}`} onClick={() => setArchivedState(r.id, false)} className="h-8 px-2.5 rounded flex items-center gap-1.5 text-xs border border-[#2A364F] text-cyan-400 hover:bg-cyan-500/10" title="Désarchiver"><ArchiveRestore className="w-4 h-4" />Désarchiver</button>
                  ) : (
                    <button data-testid={`archive-repair-${r.id}`} onClick={() => setArchivedState(r.id, true)} className="w-8 h-8 rounded hover:bg-[#0A0D14] text-[#94A3B8] hover:text-amber-400 flex items-center justify-center" title="Archiver"><Archive className="w-4 h-4" /></button>
                  )}
                  <button data-testid={`preview-repair-${r.id}`} onClick={() => setPreview(r)} className="w-8 h-8 rounded hover:bg-[#0A0D14] text-[#94A3B8] hover:text-cyan-400 flex items-center justify-center" title="Aperçu"><Search className="w-4 h-4" /></button>
                  <button data-testid={`repair-pdf-${r.id}`} onClick={() => downloadPdf(r)} className="w-8 h-8 rounded hover:bg-[#0A0D14] text-[#94A3B8] hover:text-[#10B981] flex items-center justify-center" title="Ordre de réparation PDF"><Download className="w-4 h-4" /></button>
                  <button data-testid={`edit-repair-${r.id}`} onClick={() => openEdit(r)} className="w-8 h-8 rounded hover:bg-[#0A0D14] text-[#94A3B8] hover:text-[#3B82F6] flex items-center justify-center"><Pencil className="w-4 h-4" /></button>
                  {isFull && <button data-testid={`delete-repair-${r.id}`} onClick={() => del(r.id)} className="w-8 h-8 rounded hover:bg-[#0A0D14] text-[#94A3B8] hover:text-[#EF4444] flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>}
                </div>
              </div>
            </div>
          );
        })}
        {(view === "archive" ? archived : repairs).length === 0 && <div className="text-center text-[#64748B] py-10">{view === "archive" ? "Aucune fiche archivée." : "Aucune fiche de réparation."}</div>}
      </div>

      {view === "atelier" && (
        <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-3" data-testid="kanban-board">
          {Object.entries(STATUTS).map(([key, st]) => {
            const col = repairs.filter((r) => r.statut === key);
            return (
              <div
                key={key}
                data-testid={`kanban-col-${key}`}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => { if (dragId) changeStatus(dragId, key); setDragId(null); }}
                className="bg-[#0A0D14] border border-[#2A364F] rounded-xl p-3 min-h-[200px]"
              >
                <div className="flex items-center justify-between mb-3">
                  <span className={`text-[11px] px-2 py-1 rounded border ${st.color}`}>{st.label}</span>
                  <span className="text-xs text-[#64748B] font-mono">{col.length}</span>
                </div>
                <div className="space-y-2">
                  {col.map((r) => {
                    const totMO = (r.main_oeuvre || []).reduce((a, m) => a + m.heures * m.taux, 0);
                    const totP = (r.pieces_utilisees || []).reduce((a, p) => a + p.quantite * p.prix, 0);
                    return (
                      <div
                        key={r.id}
                        data-testid={`kanban-card-${r.id}`}
                        draggable
                        onDragStart={() => setDragId(r.id)}
                        onDragEnd={() => setDragId(null)}
                        onClick={() => openEdit(r)}
                        className={`bg-[#182232] border border-[#2A364F] rounded-lg p-3 cursor-grab active:cursor-grabbing hover:border-[#3B82F6]/50 transition-colors ${dragId === r.id ? "opacity-50" : ""}`}
                      >
                        <div className="text-sm font-semibold text-[#F8FAFC] flex items-center gap-1.5"><Car className="w-3.5 h-3.5 text-cyan-400" />{r.vehicle?.marque} {r.vehicle?.modele}</div>
                        {r.vehicle?.immatriculation && <div className="mt-1"><span className="font-mono font-bold tracking-widest text-yellow-400 bg-slate-900 px-1.5 py-0.5 rounded border border-yellow-500/30 uppercase text-[10px]">{r.vehicle.immatriculation}</span></div>}
                        <div className="text-xs text-[#64748B] mt-1.5 line-clamp-2">{r.diagnostic || "—"}</div>
                        <PiecesApercu r={r} compact />
                        <div className="text-xs font-mono text-[#F59E0B] mt-2">{(totP + totMO).toFixed(2)} €</div>
                        {r.devis_numero && <div className="mt-1 text-[10px] text-blue-400 flex items-center gap-1"><FileText className="w-3 h-3" />Devis {r.devis_numero}</div>}
                        <button data-testid={`preview-repair-kanban-${r.id}`} onClick={(e) => { e.stopPropagation(); setPreview(r); }} className="mt-2 text-[11px] text-[#64748B] hover:text-cyan-400 flex items-center gap-1"><Search className="w-3 h-3" />Aperçu</button>
                      </div>
                    );
                  })}
                  {col.length === 0 && <div className="text-xs text-[#64748B] text-center py-4 border border-dashed border-[#2A364F] rounded-lg">Déposer ici</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="bg-[#121824] border-[#2A364F] text-[#F8FAFC] max-w-xl max-h-[90vh] overflow-y-auto" data-testid="repair-preview-dialog">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Search className="w-5 h-5 text-cyan-400" />Aperçu de la fiche</DialogTitle></DialogHeader>
          {preview && (() => {
            const totP = (preview.pieces_utilisees || []).reduce((a, p) => a + p.quantite * p.prix, 0);
            const totMO = (preview.main_oeuvre || []).reduce((a, m) => a + m.heures * m.taux, 0);
            const st = STATUTS[preview.statut] || STATUTS.diagnostic;
            return (
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[11px] px-2 py-0.5 rounded border ${st.color}`}>{st.label}</span>
                  {preview.devis_numero && <button onClick={() => { setPreview(null); navigate("/devis"); }} className="text-[10px] px-2 py-0.5 rounded border bg-blue-500/10 text-blue-400 border-blue-500/30 flex items-center gap-1"><FileText className="w-3 h-3" />Issu du devis {preview.devis_numero}</button>}
                </div>
                <div className="text-[#CBD5E1]"><Car className="w-4 h-4 inline text-cyan-400 mr-1" />{vLabel(preview.vehicle)} {preview.vehicle?.immatriculation ? `· ${preview.vehicle.immatriculation}` : ""}</div>
                <div className="text-[#94A3B8]">{preview.client?.nom ? `Client : ${preview.client.nom} · ` : ""}{(preview.kilometrage || 0).toLocaleString("fr-FR")} km · Mécano : {preview.mecanicien || "—"}</div>
                {preview.diagnostic && <div className="text-[#94A3B8]">🔧 {preview.diagnostic}</div>}
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-[#64748B] mb-1">Pièces</div>
                  {(preview.pieces_utilisees || []).length === 0 ? <div className="text-xs text-[#64748B] italic">Aucune pièce</div> :
                    (preview.pieces_utilisees || []).map((p, i) => (
                      <div key={i} className="flex justify-between border-b border-[#2A364F]/50 py-1">
                        <span className="text-[#CBD5E1]">{p.designation} <span className="text-[#64748B]">×{p.quantite}</span>{p.source === "vhu" && <span className="ml-1 text-blue-400 text-[10px]">VHU</span>}{p.mo_incluse === false && <span className="ml-1 text-amber-400 text-[10px]">MO+</span>}</span>
                        <span className="font-mono text-[#94A3B8]">{(p.quantite * p.prix).toFixed(2)} €</span>
                      </div>
                    ))}
                </div>
                {(preview.main_oeuvre || []).length > 0 && (
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-[#64748B] mb-1">Main d'œuvre</div>
                    {(preview.main_oeuvre || []).map((m, i) => (
                      <div key={i} className="flex justify-between border-b border-[#2A364F]/50 py-1">
                        <span className="text-[#CBD5E1]">{m.description || "—"} <span className="text-[#64748B]">{m.heures}h × {m.taux}€</span></span>
                        <span className="font-mono text-[#94A3B8]">{(m.heures * m.taux).toFixed(2)} €</span>
                      </div>
                    ))}
                  </div>
                )}
                {preview.commentaires && <div className="text-[#94A3B8] text-xs">💬 {preview.commentaires}</div>}
                <div className="bg-[#0A0D14] border border-[#2A364F] rounded-lg p-3 space-y-1">
                  <div className="flex justify-between text-[#94A3B8]"><span>Pièces</span><span className="font-mono">{totP.toFixed(2)} €</span></div>
                  <div className="flex justify-between text-[#94A3B8]"><span>Main d'œuvre</span><span className="font-mono">{totMO.toFixed(2)} €</span></div>
                  <div className="flex justify-between font-bold pt-1 border-t border-[#2A364F]"><span>Total</span><span className="font-mono text-[#F59E0B]">{(totP + totMO).toFixed(2)} €</span></div>
                </div>
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreview(null)} className="border-[#2A364F] bg-transparent text-[#94A3B8]">Fermer</Button>
            {preview && <Button data-testid="preview-edit-button" onClick={() => { const r = preview; setPreview(null); openEdit(r); }} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white">Modifier</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#121824] border-[#2A364F] text-[#F8FAFC] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{editId ? "Modifier la fiche" : "Nouvelle fiche de réparation"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <div className="flex items-center justify-between">
                  <Label className="text-[#94A3B8]">Véhicule *</Label>
                  <Button type="button" data-testid="quick-vehicle-button" size="sm" variant="outline" onClick={() => setVehOpen(true)} className="h-7 border-[#2A364F] bg-transparent text-cyan-400 hover:text-cyan-300"><CarFront className="w-3.5 h-3.5 mr-1" />Nouveau véhicule</Button>
                </div>
                <Select value={form.vehicle_id} onValueChange={onVehicleChange}>
                  <SelectTrigger data-testid="repair-vehicle-select" className="mt-1 bg-[#182232] border-[#2A364F]"><SelectValue placeholder="Choisir un véhicule" /></SelectTrigger>
                  <SelectContent>
                    {vehicles.map((v) => {
                      const cli = clients.find((c) => c.id === v.client_id);
                      return <SelectItem key={v.id} value={v.id}>{v.marque} {v.modele} — {v.immatriculation}{cli ? ` (${cli.nom})` : " (non rattaché)"}</SelectItem>;
                    })}
                  </SelectContent>
                </Select>
                {vehicles.length === 0 && <p className="text-xs text-amber-400 mt-1">Aucun véhicule. Cliquez « Nouveau véhicule » pour en créer un rapidement.</p>}
              </div>
              <div>
                <Label className="text-[#94A3B8]">Statut</Label>
                <Select value={form.statut} onValueChange={(v) => setForm({ ...form, statut: v })}>
                  <SelectTrigger data-testid="repair-statut-select" className="mt-1 bg-[#182232] border-[#2A364F]"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(STATUTS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-[#94A3B8]">Kilométrage</Label><Input type="number" value={form.kilometrage} onChange={(e) => setForm({ ...form, kilometrage: e.target.value })} className="mt-1 bg-[#182232] border-[#2A364F]" /></div>
            </div>

            <div><Label className="text-[#94A3B8]">Diagnostic</Label><Textarea data-testid="repair-diagnostic-input" value={form.diagnostic} onChange={(e) => setForm({ ...form, diagnostic: e.target.value })} className="mt-1 bg-[#182232] border-[#2A364F]" rows={2} /></div>

            {/* Main d'oeuvre */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-[#94A3B8]">Main d'œuvre</Label>
                <Button type="button" size="sm" variant="outline" onClick={addMO} className="h-7 border-[#2A364F] bg-transparent text-[#94A3B8]"><Plus className="w-3 h-3 mr-1" />Ligne</Button>
              </div>
              <div className="space-y-2">
                {form.main_oeuvre.map((m, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <Input value={m.description} onChange={(e) => setMO(i, "description", e.target.value)} placeholder="Description" className="bg-[#182232] border-[#2A364F] flex-1" />
                    <Input type="number" value={m.heures} onChange={(e) => setMO(i, "heures", e.target.value)} className="bg-[#182232] border-[#2A364F] w-20" title="Heures" />
                    <Input type="number" value={m.taux} onChange={(e) => setMO(i, "taux", e.target.value)} className="bg-[#182232] border-[#2A364F] w-20" title="Taux €/h" />
                    <button onClick={() => rmMO(i)} className="text-[#64748B] hover:text-[#EF4444]"><X className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            </div>

            {/* Pieces */}
            <div>
              <Label className="text-[#94A3B8]">Pièces / pneus consommés (décrémente le stock)</Label>
              <div className="relative mt-1">
                <Input
                  data-testid="repair-part-search"
                  value={partQuery}
                  onChange={(e) => setPartQuery(e.target.value)}
                  placeholder="🔍 Rechercher une pièce par référence ou désignation (stock + VHU)…"
                  className="bg-[#182232] border-[#2A364F]"
                />
                {partQuery.trim().length >= 2 && (
                  <div data-testid="repair-part-results" className="absolute z-20 mt-1 w-full max-h-72 overflow-auto rounded-lg border border-[#2A364F] bg-[#121824] shadow-xl">
                    {partSearching && <div className="px-3 py-2 text-xs text-[#64748B]">Recherche…</div>}
                    {!partSearching && partResults.length === 0 && <div className="px-3 py-2 text-xs text-[#64748B]">Aucune pièce trouvée</div>}
                    {partResults.map((r, i) => (
                      <button
                        key={i}
                        type="button"
                        data-testid={`repair-part-result-${i}`}
                        onClick={() => addPartFromSearch(r)}
                        className="w-full text-left px-3 py-2 hover:bg-[#1E2A3E] border-b border-[#2A364F]/50 last:border-0 flex items-center gap-2"
                      >
                        <span className={`shrink-0 text-[9px] px-1.5 py-0.5 rounded border ${r.source === "vhu" ? "bg-blue-500/10 text-blue-400 border-blue-500/30" : "bg-emerald-500/10 text-emerald-400 border-emerald-500/30"}`}>
                          {r.source === "vhu" ? "VHU" : "STOCK"}
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="block text-sm text-[#F8FAFC] truncate">{r.designation}</span>
                          <span className="block text-[11px] text-[#64748B] truncate">
                            {r.reference && <span className="font-mono">{r.reference}</span>}
                            {r.source === "vhu" ? (r.vhu_label ? ` · ${r.vhu_label}` : "") : ` · stock ${r.quantite}`}
                            {r.emplacement ? ` · 📍${r.emplacement}` : ""}
                          </span>
                        </span>
                        <span className="shrink-0 text-xs font-mono text-[#94A3B8]">{Number(r.prix || 0).toFixed(2)} €</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Piece à commander (catalogue ou libre) */}
              <div className="flex flex-wrap gap-2 mt-2">
                <Select value="" onValueChange={(v) => { const c = catalog.find((x) => x.designation === v); setOrder({ ...order, designation: v, reference: c?.reference || "", prix: c?.prix || 0 }); }}>
                  <SelectTrigger data-testid="order-catalog-select" className="bg-[#182232] border-[#2A364F] w-52"><SelectValue placeholder="Depuis le catalogue…" /></SelectTrigger>
                  <SelectContent>
                    {catalog.length === 0 && <div className="px-3 py-2 text-xs text-[#64748B]">Catalogue vide</div>}
                    {catalog.map((c) => <SelectItem key={c.id} value={c.designation}>{c.designation}{c.reference ? ` · ${c.reference}` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input data-testid="order-piece-designation" value={order.designation} onChange={(e) => setOrder({ ...order, designation: e.target.value })} placeholder="…ou saisir librement" className="bg-[#182232] border-[#2A364F] flex-1 min-w-[160px]" />
                <Input type="number" min={1} value={order.quantite} onChange={(e) => setOrder({ ...order, quantite: e.target.value })} className="bg-[#182232] border-[#2A364F] w-16" title="Qté" />
                <Button type="button" data-testid="repair-add-order-button" onClick={addOrderPiece} disabled={!order.designation.trim()} variant="outline" className="border-[#2A364F] bg-transparent text-amber-400 hover:text-amber-300">À commander</Button>
              </div>
              <p className="text-[11px] text-[#64748B] mt-1">Choisissez dans le catalogue ou saisissez librement — les nouvelles pièces sont ajoutées au catalogue. Le prix pourra être renseigné plus tard par le secrétariat.</p>

              <div className="space-y-2 mt-2">
                {form.pieces_utilisees.map((p, i) => (
                  <div key={i} data-testid={`repair-piece-row-${i}`} className="flex gap-2 items-center bg-[#0A0D14] border border-[#2A364F] rounded-lg px-3 py-2">
                    <div className="flex-1 text-sm text-[#F8FAFC]">
                      {p.designation} <span className="text-xs text-[#64748B] font-mono">{p.reference}</span>
                      {p.source === "vhu" && <span className="ml-2 whitespace-nowrap text-[10px] px-1.5 py-0.5 rounded border bg-blue-500/10 text-blue-400 border-blue-500/30">Pièce VHU</span>}
                      {!p.stock_id && p.source !== "vhu" && <span className="ml-2 whitespace-nowrap text-[10px] px-1.5 py-0.5 rounded border bg-amber-500/10 text-amber-400 border-amber-500/30">À commander</span>}
                    </div>
                    <button type="button" data-testid={`piece-mo-toggle-${i}`} onClick={() => setPieceMO(i, p.mo_incluse === false ? true : false)} title="Main d'œuvre incluse dans le prix de la pièce" className={`shrink-0 text-[10px] px-2 py-1 rounded border whitespace-nowrap ${p.mo_incluse === false ? "bg-amber-500/10 text-amber-400 border-amber-500/30" : "bg-emerald-500/10 text-emerald-400 border-emerald-500/30"}`}>
                      {p.mo_incluse === false ? "MO en sus" : "MO incluse"}
                    </button>
                    <Input type="number" min={1} value={p.quantite} onChange={(e) => setPieceQty(i, e.target.value)} className="bg-[#182232] border-[#2A364F] w-14" title="Qté" />
                    <div className="relative w-24">
                      <Input type="number" step="0.01" value={p.prix} onChange={(e) => setPiecePrix(i, e.target.value)} className="bg-[#182232] border-[#2A364F] pr-5" title="Prix unitaire (€)" placeholder="Prix" />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-[#64748B]">€</span>
                    </div>
                    <span className="text-sm font-mono text-[#94A3B8] w-20 text-right">{(p.quantite * p.prix).toFixed(2)} €</span>
                    <button onClick={() => rmPiece(i)} className="text-[#64748B] hover:text-[#EF4444]"><X className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            </div>

            <div><Label className="text-[#94A3B8]">Commentaires du mécanicien</Label><Textarea value={form.commentaires} onChange={(e) => setForm({ ...form, commentaires: e.target.value })} className="mt-1 bg-[#182232] border-[#2A364F]" rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-[#2A364F] bg-transparent text-[#94A3B8]">Annuler</Button>
            <Button data-testid="save-repair-button" onClick={save} disabled={!form.vehicle_id} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick vehicle create (mécanicien) */}
      <Dialog open={vehOpen} onOpenChange={setVehOpen}>
        <DialogContent className="bg-[#121824] border-[#2A364F] text-[#F8FAFC]">
          <DialogHeader><DialogTitle className="font-heading">Nouveau véhicule (rapide)</DialogTitle></DialogHeader>
          <p className="text-xs text-[#94A3B8]">Créez la fiche véhicule sans client — le secrétariat le rattachera plus tard.</p>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-[#94A3B8]">Marque *</Label><Input data-testid="quick-veh-marque" value={vehForm.marque} onChange={(e) => setVehForm({ ...vehForm, marque: e.target.value })} className="mt-1 bg-[#182232] border-[#2A364F]" /></div>
            <div><Label className="text-[#94A3B8]">Modèle</Label><Input value={vehForm.modele} onChange={(e) => setVehForm({ ...vehForm, modele: e.target.value })} className="mt-1 bg-[#182232] border-[#2A364F]" /></div>
            <div className="col-span-2"><Label className="text-[#94A3B8]">Immatriculation *</Label><Input data-testid="quick-veh-immat" value={vehForm.immatriculation} onChange={(e) => setVehForm({ ...vehForm, immatriculation: e.target.value.toUpperCase() })} className="mt-1 bg-[#182232] border-[#2A364F] font-mono" placeholder="AA-123-BB" /></div>
            <div><Label className="text-[#94A3B8]">Année</Label><Input value={vehForm.annee} onChange={(e) => setVehForm({ ...vehForm, annee: e.target.value })} className="mt-1 bg-[#182232] border-[#2A364F]" /></div>
            <div><Label className="text-[#94A3B8]">Kilométrage</Label><Input type="number" value={vehForm.kilometrage} onChange={(e) => setVehForm({ ...vehForm, kilometrage: e.target.value })} className="mt-1 bg-[#182232] border-[#2A364F]" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVehOpen(false)} className="border-[#2A364F] bg-transparent text-[#94A3B8]">Annuler</Button>
            <Button data-testid="save-quick-vehicle-button" onClick={saveVehicle} disabled={!vehForm.marque || !vehForm.immatriculation} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white">Créer & sélectionner</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick add "pièce à commander" (accès rapide par ligne) */}
      <Dialog open={!!quickId} onOpenChange={(o) => { if (!o) setQuickId(null); }}>
        <DialogContent className="bg-[#121824] border-[#2A364F] text-[#F8FAFC]">
          <DialogHeader><DialogTitle className="font-heading">Ajouter une pièce à commander</DialogTitle></DialogHeader>
          <p className="text-xs text-[#94A3B8]">Ajout rapide à la fiche sans l'ouvrir. Choisissez dans le catalogue ou saisissez librement.</p>
          <div className="flex flex-wrap gap-2">
            <Select value="" onValueChange={(v) => { const c = catalog.find((x) => x.designation === v); setQuickPiece({ ...quickPiece, designation: v }); }}>
              <SelectTrigger data-testid="quick-order-catalog-select" className="bg-[#182232] border-[#2A364F] w-full"><SelectValue placeholder="Depuis le catalogue…" /></SelectTrigger>
              <SelectContent>
                {catalog.length === 0 && <div className="px-3 py-2 text-xs text-[#64748B]">Catalogue vide</div>}
                {catalog.map((c) => <SelectItem key={c.id} value={c.designation}>{c.designation}{c.reference ? ` · ${c.reference}` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input data-testid="quick-order-designation" value={quickPiece.designation} onChange={(e) => setQuickPiece({ ...quickPiece, designation: e.target.value })} placeholder="…ou saisir librement" className="bg-[#182232] border-[#2A364F] flex-1 min-w-[160px]" />
            <Input type="number" min={1} value={quickPiece.quantite} onChange={(e) => setQuickPiece({ ...quickPiece, quantite: e.target.value })} className="bg-[#182232] border-[#2A364F] w-16" title="Qté" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setQuickId(null)} className="border-[#2A364F] bg-transparent text-[#94A3B8]">Annuler</Button>
            <Button data-testid="quick-order-save" onClick={quickAddOrder} disabled={!quickPiece.designation.trim()} className="bg-amber-500 hover:bg-amber-600 text-[#0A0D14] font-semibold">Ajouter à la fiche</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
